// Survey trigger logic + snooze state.
//
// Trigger rule: show after either 30 minutes of session time OR 3 fully
// completed Operating Model sections (current + future state both have
// data), once per user. Dismissal snoozes for 48 hours.
//
// Storage:
//   guruone_survey_shown        — 'true' once submitted; never re-asked
//   guruone_survey_snoozed_until — epoch ms; suppresses prompt until past
//   guruone_session_start        — epoch ms; first time the page mounted

const SURVEY_SHOWN_KEY = 'guruone_survey_shown';
const SURVEY_SNOOZED_KEY = 'guruone_survey_snoozed_until';
const SESSION_START_KEY = 'guruone_session_start';
const OM_STORAGE_KEY = 'guruone_operating_model';

export function initSessionTimer() {
  if (!localStorage.getItem(SESSION_START_KEY)) {
    localStorage.setItem(SESSION_START_KEY, Date.now().toString());
  }
}

export function getSessionMinutes() {
  const start = parseInt(localStorage.getItem(SESSION_START_KEY) || '0', 10);
  return start ? Math.floor((Date.now() - start) / 60000) : 0;
}

export function hasSurveyBeenShown() {
  return localStorage.getItem(SURVEY_SHOWN_KEY) === 'true';
}

export function markSurveyShown() {
  localStorage.setItem(SURVEY_SHOWN_KEY, 'true');
}

export function isSurveySnoozed() {
  const snoozeUntil = parseInt(localStorage.getItem(SURVEY_SNOOZED_KEY) || '0', 10);
  return snoozeUntil > Date.now();
}

export function snoozeSurvey() {
  const fortyEightHours = Date.now() + 48 * 60 * 60 * 1000;
  localStorage.setItem(SURVEY_SNOOZED_KEY, fortyEightHours.toString());
}

export function shouldShowSurvey(completedSectionCount) {
  if (hasSurveyBeenShown()) return false;
  if (isSurveySnoozed()) return false;
  const timeThresholdMet = getSessionMinutes() >= 30;
  const sectionThresholdMet = completedSectionCount >= 3;
  return timeThresholdMet || sectionThresholdMet;
}

// ── Completed-section count ─────────────────────────────────────
// A section "counts" when its current and future objects each carry
// real user-entered data. We deliberately avoid importing OM_SECTIONS
// from OperatingModelCanvas.js so this module stays small and the
// canvas file isn't refactored — at the cost of a slightly looser
// definition of "filled" than the canvas's own isSectionFilled.
//
// Keys starting with '_' are treated as private (e.g. transcript
// records like _people_current_transcript) and ignored, so a saved
// transcript without any extracted answers doesn't count as filled.
function hasMeaningfulData(obj) {
  if (!obj || typeof obj !== 'object') return false;
  return Object.entries(obj).some(([k, v]) => {
    if (k.startsWith('_')) return false;
    if (v == null) return false;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'object') return hasMeaningfulData(v);
    return String(v).trim().length > 0;
  });
}

export function getCompletedSectionCount() {
  try {
    const raw = localStorage.getItem(OM_STORAGE_KEY);
    if (!raw) return 0;
    const omData = JSON.parse(raw);
    if (!omData || typeof omData !== 'object') return 0;
    return Object.values(omData).filter(section =>
      hasMeaningfulData(section?.current) && hasMeaningfulData(section?.future)
    ).length;
  } catch {
    return 0;
  }
}
